import React, { useEffect, useRef } from 'react';
import './Modal.css';

/**
 * Accessible modal with focus trap, escape-to-close, backdrop click.
 *   <Modal open={open} onClose={fn} title="Confirm delete">
 *     <p>Are you sure?</p>
 *     <Modal.Actions>
 *       <Button onClick={...}>Cancel</Button>
 *       <Button variant="danger" onClick={...}>Delete</Button>
 *     </Modal.Actions>
 *   </Modal>
 */
export default function Modal({ open, onClose, title, children, size = 'md', closeOnBackdrop = true }) {
  const dialogRef = useRef(null);
  const previousActive = useRef(null);

  useEffect(() => {
    if (!open) return;
    previousActive.current = document.activeElement;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); }
      if (e.key === 'Tab' && dialogRef.current) {
        // Basic focus trap
        const focusables = dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last  = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
        else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
      }
    };
    document.addEventListener('keydown', onKey);
    setTimeout(() => dialogRef.current?.querySelector('button, [tabindex]')?.focus(), 0);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previousActive.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={closeOnBackdrop ? onClose : undefined}>
      <div
        ref={dialogRef}
        className={`modal modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        onClick={e => e.stopPropagation()}
      >
        {title && (
          <header className="modal__header">
            <h2 id="modal-title" className="modal__title">{title}</h2>
            <button type="button" className="modal__close" onClick={onClose} aria-label="Close">×</button>
          </header>
        )}
        <div className="modal__body">{children}</div>
      </div>
    </div>
  );
}

Modal.Actions = function ModalActions({ children }) {
  return <div className="modal__actions">{children}</div>;
};
